import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, reactive, ref, shallowRef, toRef, toValue, unref, useId, watch, watchEffect } from "vue";
//#region src/utils/builder.ts
/**
* We use this identity function to ensure the correct typings of the headless composables
*
* @example
*   ```ts
*   export const createTooltip = createBuilder(({ initialVisible }: CreateTooltipOptions) => {
*     const tooltipId = useId();
*     const isVisible = ref(initialVisible);
*
*     const hoverEvents = {
*       onMouseover: () => (isVisible.value = true),
*       onMouseout: () => (isVisible.value = false),
*       onFocusin: () => (isVisible.value = true),
*       onFocusout: () => (isVisible.value = false),
*     };
*
*     return {
*       elements: {
*         trigger: {
*           "aria-describedby": tooltipId,
*           ...hoverEvents,
*         },
*         tooltip: {
*           role: "tooltip",
*           id: tooltipId,
*           tabindex: "-1",
*           ...hoverEvents,
*         },
*       },
*       state: {
*         isVisible,
*       },
*     };
*   });
*   ```;
*/
var createBuilder = (builder) => builder;
/**
* Creates a special writeable computed that references a DOM Element.
* Vue Component references will be unwrapped.
*
* @example
*   ```ts
*   createBuilder() => {
*   const buttonRef = createElRef<HtmlButtonElement>();
*   return {
*   elements: {
*   button: {
*   ref: buttonRef,
*   },
*   }
*   };
*   });
*   ```;
*/
function createElRef() {
	const elementRef = shallowRef(null);
	return computed({
		set: (ref) => {
			const element = Array.isArray(ref) ? ref[0] : ref;
			elementRef.value = getNativeElement(element);
		},
		get: () => elementRef.value
	});
}
var getNativeElement = (element) => {
	if (element && typeof element === "object" && "$el" in element) return element.$el;
	return element ?? null;
};
//#endregion
//#region src/utils/dates.ts
function getISOWeekNumber(date) {
	const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
	const dayNum = d.getUTCDay() || 7;
	d.setUTCDate(d.getUTCDate() + 4 - dayNum);
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
	return Math.ceil(((d.getTime() - yearStart.getTime()) / 864e5 + 1) / 7);
}
/**
* Checks whether the given date is in between the given start and end date.
*/
function isInDateRange(date, start, end) {
	start = new Date(start);
	start.setHours(0, 0, 0, 0);
	end = new Date(end);
	end.setHours(23, 59, 59, 999);
	const time = date.getTime();
	return time >= start.getTime() && time <= end.getTime();
}
function getNormalizedDayIndex(date, weekStartDay) {
	const day = date.getDay();
	const start = WEEKDAYS.indexOf(weekStartDay);
	return ((day === 0 ? 6 : day - 1) - start + 7) % 7;
}
var WEEKDAYS = [
	"Monday",
	"Tuesday",
	"Wednesday",
	"Thursday",
	"Friday",
	"Saturday",
	"Sunday"
];
function sortDateRange(range) {
	const start = new Date(range.start);
	const end = range.end ? new Date(range.end) : void 0;
	if (end && end.getTime() < start.getTime()) return {
		start: end,
		end: start
	};
	return {
		start,
		end
	};
}
//#endregion
//#region src/composables/calendar/createCalendar.ts
var createCalendar = createBuilder((options) => {
	const viewMonth = computed({
		get: () => {
			const date = toValue(options.viewMonth);
			return date ? new Date(date) : /* @__PURE__ */ new Date();
		},
		set: (newValue) => options.onUpdateViewMonth?.(new Date(newValue))
	});
	/**
	* Human readable weekday names depending on the given locale.
	*/
	const weekdayNames = computed(() => {
		const formatter = new Intl.DateTimeFormat(toValue(options.locale), { weekday: toValue(options.calendarSize) === "big" ? "long" : "short" });
		const names = Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i)).map((day) => formatter.format(day));
		const weekStartDay = toValue(options.weekStartDay);
		const index = WEEKDAYS.indexOf(weekStartDay);
		return names.slice(index).concat(names.slice(0, index));
	});
	const initialFocusedDate = () => {
		const today = /* @__PURE__ */ new Date();
		const view = viewMonth.value;
		if (today.getFullYear() === view.getFullYear() && today.getMonth() === view.getMonth()) return today;
		else return new Date(view.getFullYear(), view.getMonth(), 1);
	};
	const focusedDate = ref(initialFocusedDate());
	watch(() => toValue(options.modelValue), (newValue) => {
		if (!newValue) return;
		let newFocusDate;
		if (Array.isArray(newValue)) newFocusDate = newValue.length ? new Date(newValue[0]) : void 0;
		else if (typeof newValue === "object" && !(newValue instanceof Date)) newFocusDate = new Date(newValue.start);
		else newFocusDate = new Date(newValue);
		if (newFocusDate) {
			focusedDate.value = newFocusDate;
			viewMonth.value = newFocusDate;
		}
	}, { immediate: true });
	const isToday = (date) => {
		return date.toDateString() === (/* @__PURE__ */ new Date()).toDateString();
	};
	const isSelected = computed(() => {
		return (date) => {
			const value = toValue(options.modelValue);
			if (!value) return false;
			if (Array.isArray(value)) return value.map((i) => new Date(i)).some((d) => d.toDateString() === date.toDateString());
			if (typeof value === "object" && !(value instanceof Date)) {
				const start = new Date(value.start);
				const end = value.end ? new Date(value.end) : void 0;
				return start.toDateString() === date.toDateString() || end?.toDateString() === date.toDateString();
			}
			return new Date(value).toDateString() === date.toDateString();
		};
	});
	const isFocused = computed(() => {
		return (date) => {
			return focusedDate.value?.toDateString() === date.toDateString();
		};
	});
	const isDisabled = computed(() => {
		return (date) => {
			const disabledPropValue = toValue(options.disabled);
			if (typeof disabledPropValue === "boolean") {
				if (disabledPropValue) return true;
			}
			if (typeof disabledPropValue === "function") {
				if (disabledPropValue(date)) return true;
			}
			const min = toValue(options.min);
			const minDate = min ? new Date(min) : void 0;
			minDate?.setHours(0, 0, 0, 0);
			const max = toValue(options.max);
			const maxDate = max ? new Date(max) : void 0;
			maxDate?.setHours(23, 59, 59, 999);
			if (minDate && maxDate) return !isInDateRange(date, minDate, maxDate);
			if (minDate) return date.getTime() < minDate.getTime();
			if (maxDate) return date.getTime() > maxDate.getTime();
			return false;
		};
	});
	const weeksToRender = computed(() => {
		const weekStartDay = toValue(options.weekStartDay);
		const firstDayInViewMonth = new Date(viewMonth.value.getFullYear(), viewMonth.value.getMonth(), 1);
		const daysBeforeStart = ((firstDayInViewMonth.getDay() + 6) % 7 - WEEKDAYS.indexOf(weekStartDay) + 7) % 7;
		const weeksToRender = 6;
		const weeks = [];
		for (let weekIndex = 0; weekIndex < weeksToRender; weekIndex++) {
			const weekStartDate = new Date(firstDayInViewMonth);
			weekStartDate.setDate(weekStartDate.getDate() - daysBeforeStart + weekIndex * 7);
			const days = Array.from({ length: 7 }, (_, dayIndex) => {
				const date = new Date(weekStartDate);
				date.setDate(date.getDate() + dayIndex);
				return {
					date,
					isCurrentMonth: date.getMonth() === viewMonth.value.getMonth()
				};
			});
			weeks.push({
				weekNumber: getISOWeekNumber(weekStartDate),
				days
			});
		}
		return weeks;
	});
	const goToDate = (date, preventSelectionUpdate) => {
		focusedDate.value = new Date(date);
		if (focusedDate.value.getFullYear() !== viewMonth.value.getFullYear() || focusedDate.value.getMonth() !== viewMonth.value.getMonth()) viewMonth.value = new Date(focusedDate.value);
		const selectionMode = toValue(options.selectionMode);
		if (!selectionMode || preventSelectionUpdate) return;
		const selection = toValue(options.modelValue);
		switch (selectionMode) {
			case "single":
				options.onUpdateModelValue?.(new Date(date));
				break;
			case "multiple": {
				let values = Array.isArray(selection) ? selection.map((d) => new Date(d)) : [];
				if (isSelected.value(date)) values = values.filter((d) => d.toDateString() !== date.toDateString());
				else values.push(new Date(date));
				options.onUpdateModelValue?.(values);
				break;
			}
			case "range": {
				const currentRange = typeof selection === "object" && !(selection instanceof Date) && !Array.isArray(selection) ? selection : void 0;
				let newRange;
				if (currentRange?.start && currentRange.end) newRange = { start: new Date(date) };
				else newRange = {
					start: currentRange?.start ? new Date(currentRange.start) : new Date(date),
					end: currentRange?.start ? new Date(date) : void 0
				};
				newRange = sortDateRange(newRange);
				options.onUpdateModelValue?.(newRange);
				break;
			}
		}
	};
	const handleKeyNavigation = async (event) => {
		let newDate;
		const getNewFocusDateByDiff = (diff, type = "days") => {
			const date = new Date(focusedDate.value);
			if (type === "month") date.setMonth(date.getMonth() + diff);
			else date.setDate(date.getDate() + diff);
			return date;
		};
		switch (event.key) {
			case "ArrowUp":
				newDate = getNewFocusDateByDiff(-7);
				break;
			case "ArrowDown":
				newDate = getNewFocusDateByDiff(7);
				break;
			case "ArrowLeft":
				newDate = getNewFocusDateByDiff(-1);
				break;
			case "ArrowRight":
				newDate = getNewFocusDateByDiff(1);
				break;
			case "Home":
				newDate = getNewFocusDateByDiff(-getNormalizedDayIndex(focusedDate.value, toValue(options.weekStartDay)));
				break;
			case "End":
				newDate = getNewFocusDateByDiff(6 - getNormalizedDayIndex(focusedDate.value, toValue(options.weekStartDay)));
				break;
			case "PageUp":
				newDate = getNewFocusDateByDiff(-(event.shiftKey ? 12 : 1), "month");
				break;
			case "PageDown": newDate = getNewFocusDateByDiff(event.shiftKey ? 12 : 1, "month");
		}
		if (!newDate || isDisabled.value(newDate)) return;
		event.preventDefault();
		goToDate(newDate, true);
		await nextTick();
		tableRef.value?.querySelector(`[data-date="${focusedDate.value.toDateString()}"]`)?.focus();
	};
	const getRangeType = computed(() => {
		return (date, range) => {
			const selection = range ?? toValue(options.modelValue);
			if (!selection || typeof selection !== "object" || Array.isArray(selection) || selection instanceof Date) return;
			const sortedRange = sortDateRange(selection);
			const start = new Date(sortedRange.start);
			start.setHours(0, 0, 0, 0);
			const end = sortedRange.end ? new Date(sortedRange.end) : void 0;
			end?.setHours(23, 59, 59, 999);
			if (date.toDateString() === start.toDateString() && date.toDateString() === end?.toDateString()) return;
			if (date.toDateString() === start.toDateString()) return "start";
			if (date.toDateString() === end?.toDateString()) return "end";
			if (end && isInDateRange(date, start, end)) return "middle";
		};
	});
	const goToMonthByOffset = (offset) => {
		const date = new Date(viewMonth.value);
		date.setMonth(date.getMonth() + offset, 1);
		viewMonth.value = date;
	};
	const goToToday = () => {
		viewMonth.value = /* @__PURE__ */ new Date();
	};
	const tableRef = createElRef();
	return {
		state: {
			weekdayNames,
			weeksToRender,
			focusedDate,
			viewMonth
		},
		elements: {
			table: {
				role: "grid",
				onKeydown: handleKeyNavigation,
				ref: tableRef
			},
			cell: computed(() => (cell) => ({
				role: "gridcell",
				"aria-selected": isSelected.value(cell.date),
				"aria-disabled": isDisabled.value(cell.date)
			})),
			button: computed(() => (button) => {
				const attributes = {
					"aria-label": new Intl.DateTimeFormat(toValue(options.locale), { dateStyle: "full" }).format(button.date),
					"data-date": button.date.toDateString()
				};
				if (!toValue(options.selectionMode)) return attributes;
				const disabled = isDisabled.value(button.date);
				return {
					...attributes,
					tabindex: isFocused.value(button.date) && !disabled ? "0" : "-1",
					disabled,
					onClick: disabled ? void 0 : () => goToDate(button.date)
				};
			})
		},
		internals: {
			goToMonthByOffset,
			goToToday,
			isSelected,
			isToday,
			getRangeType,
			isDisabled,
			goToDate,
			isFocused
		}
	};
});
//#endregion
//#region src/utils/object.ts
/**
* Check if every entry of a subset exists and matches the entry of a target object.
*
* @returns `true`, if target contains the subset
*/
var isSubsetMatching = (subset, target) => Object.entries(subset).every(([key, value]) => target[key] === value);
//#endregion
//#region src/utils/keyboard.ts
/**
* Check if a specified key was pressed.
*
* @param event The KeyboardEvent
* @param key The key, either the [key
*   property](https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/key) as a string (e.g.
*   "m") or an object with the relevant key parameters, e.g. `{ key: "m", altKey: true }`
* @returns True, if the key was pressed with the specified parameters
*/
var wasKeyPressed = (event, key) => {
	if (typeof key === "string") return event.key === key;
	return isSubsetMatching({
		altKey: false,
		ctrlKey: false,
		metaKey: false,
		shiftKey: false,
		...key
	}, event);
};
var GRAPHEME_SEGMENTER = new Intl.Segmenter("en-US");
/**
* Check if the `key` property of a KeyboardEvent is a printable character.
*
* There is no standardized or specified algorithm to check for [named
* keys](https://www.w3.org/TR/uievents-key/#named-key-attribute-values) vs printable characters.
* For this check we assume that any `key` value that is a single Grapheme is a printable
* characters. This way we can ensure that combining characters and emojis are correctly recognized
* without need to keep a list of all named key.
*/
var isPrintableCharacter = (key) => [...GRAPHEME_SEGMENTER.segment(key)].length === 1;
//#endregion
//#region src/composables/helpers/useGlobalListener.ts
var GLOBAL_LISTENERS = reactive(/* @__PURE__ */ new Map());
var updateRemainingListeners = (type, remaining) => {
	if (remaining?.size) {
		GLOBAL_LISTENERS.set(type, remaining);
		return;
	}
	GLOBAL_LISTENERS.delete(type);
	document.removeEventListener(type, GLOBAL_HANDLER);
};
var removeGlobalListener = (type, listener) => {
	const globalListener = GLOBAL_LISTENERS.get(type);
	globalListener?.delete(listener);
	updateRemainingListeners(type, globalListener);
};
var addGlobalListener = (type, listener) => {
	const globalListener = GLOBAL_LISTENERS.get(type) ?? /* @__PURE__ */ new Set();
	globalListener.add(listener);
	GLOBAL_LISTENERS.set(type, globalListener);
	document.addEventListener(type, GLOBAL_HANDLER);
};
/**
* A single and unique function for all event types. We use the fact that `addEventListener` and
* `removeEventListener` are idempotent when called with the same function reference.
*/
var GLOBAL_HANDLER = (event) => {
	const type = event.type;
	GLOBAL_LISTENERS.get(type)?.forEach((cb) => cb(event));
};
var useGlobalEventListener = ({ type, listener, disabled }) => {
	onBeforeMount(() => watchEffect(() => disabled?.value ? removeGlobalListener(type, listener) : addGlobalListener(type, listener)));
	onBeforeUnmount(() => removeGlobalListener(type, listener));
};
//#endregion
//#region src/composables/helpers/useOutsideClick.ts
/**
* Composable for listening to click events that occur outside of a component.
* Useful to e.g. close flyouts or tooltips.
*/
var useOutsideClick = ({ inside, onOutsideClick, disabled, checkOnTab }) => {
	const isOutsideClick = (target) => {
		if (!target) return true;
		const raw = toValue(inside);
		return !(Array.isArray(raw) ? raw : [raw]).some((element) => {
			return getNativeElement(element)?.contains(target);
		});
	};
	/**
	* Document click handle that closes then tooltip when clicked outside.
	* Should only be called when trigger is "click".
	*/
	const clickListener = (event) => {
		if (isOutsideClick(event.target)) onOutsideClick(event);
	};
	useGlobalEventListener({
		type: "mousedown",
		listener: clickListener,
		disabled
	});
	if (checkOnTab) {
		const keydownListener = (event) => {
			if (event.key !== "Tab") return;
			if (isOutsideClick(document.activeElement)) onOutsideClick(event);
			const controller = new AbortController();
			const { signal } = controller;
			/**
			* Handles when focus enters a new element after Tab navigation.
			* Triggers outside click if focus moves outside the component.
			*/
			const onFocusIn = (event) => {
				const target = event.target;
				if (isOutsideClick(target)) onOutsideClick(event);
				controller.abort();
			};
			/**
			* Handles when the entire window loses focus during Tab navigation.
			* This covers cases like switching to dev tools or another application.
			*/
			const onWindowBlur = (event) => {
				if (isOutsideClick(document.activeElement)) onOutsideClick(event);
				controller.abort();
			};
			/**
			* Handles the Tab key release event.
			* Cleans up temporary listeners if Tab navigation is completed.
			*/
			const onKeyUp = (e) => {
				if (e.key === "Tab") controller.abort();
			};
			document.addEventListener("focusin", onFocusIn, {
				once: true,
				capture: true,
				signal
			});
			window.addEventListener("blur", onWindowBlur, {
				once: true,
				signal
			});
			document.addEventListener("keyup", onKeyUp, {
				once: true,
				capture: true,
				signal
			});
		};
		useGlobalEventListener({
			type: "keydown",
			listener: keydownListener,
			disabled
		});
	}
};
//#endregion
//#region src/utils/timer.ts
/**
* Debounces a given callback which will only be called when not called for the given timeout.
*
* @returns Callback to reset the debounce timer.
*/
var debounce = (handler, timeout) => {
	let timer;
	const func = (...lastArgs) => {
		clearTimeout(timer);
		timer = setTimeout(() => handler(...lastArgs), toValue(timeout));
	};
	/**
	* Abort the currently debounced action, if any.
	*/
	func.abort = () => clearTimeout(timer);
	return func;
};
//#endregion
//#region src/composables/helpers/useTypeAhead.ts
/**
* Enhances typeAhead to combine multiple inputs in quick succession and filter out non-printable
* characters.
*
* @example
*   ```ts
*   const typeAhead = useTypeAhead((inputString) => console.log("Typed string:", inputString));
*   // ...
*   addEventListener("keydown", typeAhead);
*   ```;
*/
var useTypeAhead = (callback, timeout = 500) => {
	let inputString = "";
	const debouncedReset = debounce(() => inputString = "", timeout);
	return (event) => {
		if (!isPrintableCharacter(event.key)) return;
		debouncedReset();
		inputString = `${inputString}${event.key}`;
		callback(inputString);
	};
};
//#endregion
//#region src/composables/listbox/createListbox.ts
/**
* Composable for creating a accessibility-conform listbox. For supported keyboard shortcuts, see:
* https://www.w3.org/WAI/ARIA/apg/patterns/listbox/examples/listbox-scrollable/
*/
var createListbox = createBuilder((options) => {
	const isMultiselect = computed(() => unref(options.multiple) ?? false);
	const isExpanded = computed(() => unref(options.isExpanded) ?? false);
	/**
	* Map for option IDs. key = option value, key = ID for the HTML element
	*/
	const descendantKeyIdMap = /* @__PURE__ */ new Map();
	const getOptionId = (value) => {
		if (!descendantKeyIdMap.has(value)) descendantKeyIdMap.set(value, useId());
		return descendantKeyIdMap.get(value);
	};
	const getOptionValueById = (id) => {
		return Array.from(descendantKeyIdMap.entries()).find(([_value, key]) => key === id)?.[0];
	};
	const getOption = (value) => {
		const id = getOptionId(value);
		return document.getElementById(id);
	};
	/**
	* Whether the listbox element is focused.
	*/
	const isFocused = ref(false);
	watchEffect(async () => {
		if (!isExpanded.value || options.activeOption.value == void 0 || !isFocused.value && !options.controlled) return;
		await nextTick();
		getOption(options.activeOption.value)?.scrollIntoView({
			block: "nearest",
			inline: "nearest"
		});
	});
	const typeAhead = useTypeAhead((inputString) => options.onTypeAhead?.(inputString));
	const handleKeydown = (event) => {
		switch (event.key) {
			case " ":
				event.preventDefault();
				if (options.activeOption.value != void 0) options.onSelect?.(options.activeOption.value, event);
				break;
			case "ArrowUp":
				event.preventDefault();
				if (options.activeOption.value == void 0) {
					options.onActivateLast?.();
					return;
				}
				options.onActivatePrevious?.(options.activeOption.value);
				break;
			case "ArrowDown":
				event.preventDefault();
				if (options.activeOption.value == void 0) {
					options.onActivateFirst?.();
					return;
				}
				options.onActivateNext?.(options.activeOption.value);
				break;
			case "Home":
				event.preventDefault();
				options.onActivateFirst?.();
				break;
			case "End":
				event.preventDefault();
				options.onActivateLast?.();
				break;
			default: typeAhead(event);
		}
	};
	return {
		elements: {
			listbox: computed(() => options.controlled ? {
				role: "listbox",
				"aria-multiselectable": isMultiselect.value,
				"aria-label": unref(options.label),
				"aria-description": options.description,
				tabindex: "-1"
			} : {
				role: "listbox",
				"aria-multiselectable": isMultiselect.value,
				"aria-label": unref(options.label),
				"aria-description": options.description,
				tabindex: "0",
				"aria-activedescendant": options.activeOption.value != void 0 ? getOptionId(options.activeOption.value) : void 0,
				onFocus: () => isFocused.value = true,
				onBlur: () => isFocused.value = false,
				onKeydown: handleKeydown
			}),
			group: computed(() => {
				return (options) => ({
					role: "group",
					"aria-label": options.label
				});
			}),
			option: computed(() => {
				return (data) => {
					const selected = data.selected ?? false;
					return {
						id: getOptionId(data.value),
						role: "option",
						"aria-label": data.label,
						"aria-disabled": data.disabled,
						"aria-checked": isMultiselect.value ? selected : void 0,
						"aria-selected": !isMultiselect.value ? selected : void 0,
						onClick: (event) => !data.disabled && options.onSelect?.(data.value, event)
					};
				};
			})
		},
		state: { isFocused },
		internals: {
			getOptionId,
			getOptionValueById,
			getOption
		}
	};
});
//#endregion
//#region src/composables/comboBox/createComboBox.ts
var OPENING_KEYS = [
	"ArrowDown",
	"ArrowUp",
	" ",
	"Enter",
	"Home",
	"End"
];
var CLOSING_KEYS = [
	"Escape",
	{
		key: "ArrowUp",
		altKey: true
	},
	"Enter",
	"Tab"
];
var SELECTING_KEYS = ["Enter"];
/**
* If the a search input is included, space should not be used to select
* TODO: idea for the future: move this distinction to the listbox?
*/
var isSelectingKey = (event, withSpace) => {
	return isKeyOfGroup(event, withSpace ? [...SELECTING_KEYS, " "] : SELECTING_KEYS);
};
var isKeyOfGroup = (event, group) => group.some((key) => wasKeyPressed(event, key));
var createComboBox = createBuilder(({ autocomplete: autocompleteRef, onAutocomplete, onTypeAhead, multiple: multipleRef, label, listLabel, listDescription, isExpanded: isExpandedRef, activeOption, onToggle, onSelect, onActivateFirst, onActivateLast, onActivateNext, onActivatePrevious, templateRef }) => {
	const controlsId = useId();
	const autocomplete = computed(() => unref(autocompleteRef));
	const isExpanded = computed(() => unref(isExpandedRef));
	const multiple = computed(() => unref(multipleRef));
	const handleInput = (event) => {
		const inputElement = event.target;
		if (autocomplete.value !== "none") onAutocomplete?.(inputElement.value);
	};
	const typeAhead = useTypeAhead((inputString) => onTypeAhead?.(inputString));
	const handleSelect = (value, event) => {
		onSelect?.(value, event);
		if (!toValue(multiple)) onToggle?.();
	};
	const handleNavigation = (event) => {
		switch (event.key) {
			case "ArrowUp":
				event.preventDefault();
				if (activeOption.value == void 0) return onActivateLast?.();
				onActivatePrevious?.(activeOption.value);
				break;
			case "ArrowDown":
				event.preventDefault();
				if (activeOption.value == void 0) return onActivateFirst?.();
				onActivateNext?.(activeOption.value);
				break;
			case "Home":
				event.preventDefault();
				onActivateFirst?.();
				break;
			case "End":
				event.preventDefault();
				onActivateLast?.();
		}
	};
	const handleKeydown = (event) => {
		if (event.key === "Enter") event.preventDefault();
		if (!isExpanded.value && isKeyOfGroup(event, OPENING_KEYS)) {
			onToggle?.();
			if (event.key === " ") event.preventDefault();
			if (event.key === "End") return onActivateLast?.();
			return onActivateFirst?.();
		}
		if (isSelectingKey(event, autocomplete.value === "none")) return handleSelect(activeOption.value);
		if (isExpanded.value && isKeyOfGroup(event, CLOSING_KEYS)) return onToggle?.();
		if (autocomplete.value === "none" && isPrintableCharacter(event.key)) {
			!isExpanded.value && onToggle?.();
			return typeAhead(event);
		}
		if (autocomplete.value !== "none" && isPrintableCharacter(event.key)) {
			!isExpanded.value && onToggle?.();
			return;
		}
		return handleNavigation(event);
	};
	const autocompleteInput = computed(() => {
		if (autocomplete.value === "none") return null;
		return {
			"aria-autocomplete": autocomplete.value,
			type: "text"
		};
	});
	const { elements: { option, group, listbox }, internals: { getOptionId, getOptionValueById, getOption } } = createListbox({
		label: listLabel,
		description: listDescription,
		multiple,
		controlled: true,
		activeOption,
		isExpanded,
		onSelect: handleSelect
	});
	useOutsideClick({
		inside: templateRef,
		onOutsideClick() {
			if (!isExpanded.value) return;
			onToggle?.(true);
		}
	});
	return {
		elements: {
			option,
			group,
			/**
			* The listbox associated with the combobox.
			*/
			listbox: computed(() => ({
				...listbox.value,
				id: controlsId,
				onMousedown: (e) => e.preventDefault()
			})),
			/**
			* An input that controls another element, that can dynamically pop-up to help the user set
			* the value of the input. The input MAY be either a single-line text field that supports
			* editing and typing or an element that only displays the current value of the combobox.
			*/
			input: computed(() => ({
				role: "combobox",
				"aria-expanded": isExpanded.value,
				"aria-controls": controlsId,
				"aria-label": unref(label),
				"aria-activedescendant": activeOption.value != void 0 ? getOptionId(activeOption.value) : void 0,
				onInput: handleInput,
				onKeydown: handleKeydown,
				...autocompleteInput.value
			})),
			/**
			* An optional button to control the visibility of the popup.
			*/
			button: computed(() => ({
				tabindex: "-1",
				onClick: () => onToggle?.()
			}))
		},
		internals: {
			getOptionId,
			getOptionValueById,
			getOption
		}
	};
});
//#endregion
//#region src/composables/helpers/useAllSettled.ts
/**
* Execute a callback, when all added promise are settled (either resolved or rejected).
* It allows for more promises to be added while waiting.
*
* @param cb Callback to execute when all added promise are settled.
* @returns An object with an add function and the active state which is true as long as any promise
*   is running.
*/
var useAllSettled = (cb) => {
	const active = ref(false);
	const allPromises = [];
	let latestPromise = Promise.resolve();
	const add = (promise) => {
		active.value = true;
		allPromises.push(promise);
		const newAllSettled = Promise.allSettled(allPromises).then(() => {
			if (newAllSettled === latestPromise) {
				active.value = false;
				allPromises.splice(0, allPromises.length);
				cb?.();
			}
		});
		latestPromise = newAllSettled;
	};
	return {
		add,
		active
	};
};
//#endregion
//#region src/composables/helpers/useLastSettled.ts
/**
* Execute a callback, when the latest promise is settled (either resolved or rejected). This
* ensures that out-of-order settling promises are ignored and only the latest promise is
* considered.
*
* @param cb Callback to execute when the last promise, that was added to the queue, is settled.
* @returns The active state of the last settled promise and a queue function to add new promises to
*   the queue.
*/
var useLastSettled = (cb) => {
	const active = ref(false);
	let lastId = -1;
	const add = (promise) => {
		const promiseId = ++lastId;
		active.value = true;
		const onFinally = (success) => (resolved) => {
			if (promiseId === lastId) {
				active.value = false;
				if (success) cb(success, resolved);
				else cb(success);
			}
		};
		promise.then(onFinally(true)).catch(onFinally(false));
	};
	const cancel = () => {
		active.value = false;
		lastId++;
	};
	return {
		active,
		add,
		cancel
	};
};
//#endregion
//#region src/composables/dataGrid/createDataGrid.ts
var COL_KEY_DATA_ATTR = "data-onyx-col-key";
var COL_INDEX_ARIA_ATTR = "aria-colindex";
var ROW_ID_DATA_ATTR = "data-onyx-row-id";
var ROW_INDEX_ARIA_ATTR = "aria-rowindex";
var StaticResolver = {
	mapCellToIndex: (cell) => Array.from(cell.closest("tr")?.cells ?? []).indexOf(cell),
	mapRowToIndex: (row) => Array.from(row.closest("table")?.rows ?? []).indexOf(row),
	resolveCell: async (cellIndex, rowIndex, table) => table.rows.item(rowIndex)?.cells.item(cellIndex),
	getTotalRows: (table) => table.rows.length,
	getTotalCols: (table) => table.rows.item(0)?.cells.length ?? 0
};
var LazyResolverFactory = ({ rows, cols, requestLazyLoad }) => ({
	mapCellToIndex: (cell) => Number(cell.getAttribute(COL_INDEX_ARIA_ATTR)) - 1,
	mapRowToIndex: (row) => Number(row.getAttribute(ROW_INDEX_ARIA_ATTR)) - 1,
	resolveCell: async (cellIndex, rowIndex, table) => {
		const queryCell = () => table.querySelector(`*[${ROW_INDEX_ARIA_ATTR}="${rowIndex + 1}"] *[${COL_INDEX_ARIA_ATTR}="${cellIndex + 1}"]`);
		let cell = queryCell();
		if (cell) return cell;
		await requestLazyLoad(cellIndex, rowIndex);
		cell = queryCell();
		if (cell) return cell;
		throw new Error(`Table cell with row index "${rowIndex}" and column index "${cellIndex}" was not found after requested lazy loading and is unable to be focused!`);
	},
	getTotalRows: () => toValue(rows),
	getTotalCols: () => toValue(cols)
});
var createDataGrid = createBuilder((options) => {
	const tableElement = createElRef();
	const lazy = options.lazy && toRef(options.lazy);
	const busy = computed(() => toValue(options.loading) ?? busySet.active.value);
	const resolver = lazy ? LazyResolverFactory({
		cols: () => lazy.value.totalCols,
		rows: () => lazy.value.totalRows,
		requestLazyLoad: lazy.value.requestLazyLoad
	}) : StaticResolver;
	const labelId = useId();
	const selectedCell = options.selectedCell || ref();
	const selectedCellEl = createElRef();
	/**
	* Tracks if the latest `resolveCell` promise is finished, if successful the focus can be moved
	* to the resolved cell.
	*/
	const focusQueue = useLastSettled((success, cell) => {
		if (success) cell?.focus();
	});
	/**
	* Tracks if any `resolveCell` promises are running.
	*/
	const busySet = useAllSettled();
	const findFirstCell = () => tableElement.value?.querySelector?.(`[${ROW_ID_DATA_ATTR}] [${COL_KEY_DATA_ATTR}]`);
	const setSelected = (element) => {
		const colKey = element.closest(`[${COL_KEY_DATA_ATTR}]`)?.getAttribute(COL_KEY_DATA_ATTR);
		const rowId = element.closest(`[${ROW_ID_DATA_ATTR}]`)?.getAttribute(ROW_ID_DATA_ATTR);
		if (colKey && rowId) selectedCell.value = {
			rowId,
			colKey
		};
	};
	const ensureTabTarget = () => {
		if (selectedCell.value && selectedCellEl.value?.isConnected) return;
		const firstCell = findFirstCell();
		if (firstCell) setSelected(firstCell);
	};
	let mutationObserver;
	onMounted(() => {
		ensureTabTarget();
		mutationObserver = new MutationObserver(ensureTabTarget);
		watch(tableElement, () => {
			if (!tableElement.value) return;
			mutationObserver?.disconnect();
			mutationObserver?.observe(tableElement.value, {
				childList: true,
				attributes: true,
				subtree: true,
				attributeFilter: ["value"]
			});
		}, { immediate: true });
	});
	onBeforeUnmount(() => {
		mutationObserver?.disconnect();
	});
	/**
	* When a cell is focused programmatically or by click, it becomes the currently selected cell.
	*/
	const onFocusin = (event) => {
		setSelected(event.target);
		focusQueue.cancel();
	};
	const onKeydown = (event) => {
		const target = event.target;
		const cellElement = target.closest("td, th");
		const rowElement = target.closest("tr");
		const tableElement = target.closest("table");
		if (!cellElement || !rowElement || !tableElement) return;
		const { getTotalRows, getTotalCols, mapRowToIndex, mapCellToIndex, resolveCell } = resolver;
		const colIndex = mapCellToIndex(cellElement);
		const rowIndex = mapRowToIndex(rowElement);
		const totalRows = getTotalRows(tableElement);
		const totalCols = getTotalCols(tableElement);
		let newColIndex = colIndex;
		let newRowIndex = rowIndex;
		if (wasKeyPressed(event, {
			ctrlKey: true,
			key: "Home"
		})) {
			newColIndex = 0;
			newRowIndex = 0;
		} else if (wasKeyPressed(event, {
			ctrlKey: true,
			key: "End"
		})) {
			newColIndex = totalCols === "unknown" ? Infinity : totalCols - 1;
			newRowIndex = totalRows === "unknown" ? Infinity : totalRows - 1;
		} else if (wasKeyPressed(event, "ArrowUp")) newRowIndex = rowIndex - 1;
		else if (wasKeyPressed(event, "ArrowDown")) newRowIndex = rowIndex + 1;
		else if (wasKeyPressed(event, "ArrowLeft")) newColIndex = colIndex - 1;
		else if (wasKeyPressed(event, "ArrowRight")) newColIndex = colIndex + 1;
		else if (wasKeyPressed(event, "Home")) newColIndex = 0;
		else if (wasKeyPressed(event, "End")) newColIndex = totalCols === "unknown" ? Infinity : totalCols - 1;
		else return;
		event.preventDefault();
		const maxRows = totalRows === "unknown" ? Infinity : totalRows - 1;
		newRowIndex = Math.max(Math.min(newRowIndex, maxRows), 0);
		const maxCols = totalCols === "unknown" ? Infinity : totalCols - 1;
		newColIndex = Math.max(Math.min(newColIndex, maxCols), 0);
		(async () => {
			const promiseResolveCell = resolveCell(newColIndex, newRowIndex, tableElement);
			focusQueue.add(promiseResolveCell);
			busySet.add(promiseResolveCell);
		})();
	};
	return {
		elements: {
			label: { id: labelId },
			table: computed(() => ({
				ref: tableElement,
				onFocusin,
				onKeydown,
				role: "grid",
				"aria-busy": busy.value,
				"aria-labelledby": labelId,
				"aria-rowcount": lazy?.value.totalRows === "unknown" ? -1 : lazy?.value.totalRows,
				"aria-colcount": lazy?.value.totalCols === "unknown" ? -1 : lazy?.value.totalCols
			})),
			tr: ({ rowId, rowIndex }) => ({
				[ROW_ID_DATA_ATTR]: rowId.toString(),
				"aria-rowindex": rowIndex == void 0 ? void 0 : rowIndex + 1,
				role: "row"
			}),
			td: computed(() => ({ rowId, colKey, colIndex }) => {
				const isSelected = colKey.toString() === selectedCell.value?.colKey.toString() && rowId.toString() === selectedCell.value?.rowId.toString();
				return {
					tabindex: isSelected ? "0" : "-1",
					ref: isSelected ? selectedCellEl : void 0,
					[COL_KEY_DATA_ATTR]: colKey.toString(),
					"aria-colindex": colIndex == void 0 ? void 0 : colIndex + 1,
					role: "cell"
				};
			})
		},
		state: { 
		/**
		* Indicates that the data grid expects a content change soon, e.g. because more or other
		* data is loaded. If `loading` is passed in via the options, this will mirror its value.
		* Otherwise it will be dynamically set based on the running state of the `requestLazyLoad`
		* promises.
		*/
busy },
		internals: {}
	};
});
//#endregion
//#region src/composables/menuButton/createMenuButton.ts
/**
* Based on https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/
*/
var createMenuButton = createBuilder((options) => {
	const rootId = useId();
	const menuId = useId();
	const rootRef = createElRef();
	const menuRef = createElRef();
	const buttonId = useId();
	const position = computed(() => toValue(options.position) ?? "bottom");
	useGlobalEventListener({
		type: "keydown",
		listener: (e) => e.key === "Escape" && setExpanded(false),
		disabled: computed(() => !options.isExpanded.value)
	});
	/**
	* Debounced expanded state that will only be toggled after a given timeout.
	*/
	const updateDebouncedExpanded = debounce(() => options.onToggle(), 200);
	watch(options.isExpanded, () => updateDebouncedExpanded.abort());
	const setExpanded = (expanded, debounced = false) => {
		if (options.disabled?.value) return;
		if (expanded === options.isExpanded.value) {
			updateDebouncedExpanded.abort();
			return;
		}
		if (debounced) {
			updateDebouncedExpanded();
			return;
		}
		options.onToggle();
	};
	const focusRelativeItem = (next) => {
		const currentMenuItem = document.activeElement;
		const currentMenu = currentMenuItem?.closest("[role=\"menu\"]") || menuRef.value;
		if (!currentMenu) return;
		const menuItems = Array.from(currentMenu.querySelectorAll("[role=\"menuitem\"]")).filter((item) => item.closest("[role=\"menu\"]") === currentMenu);
		if (position.value === "top") menuItems.reverse();
		let nextIndex = 0;
		if (currentMenuItem) {
			const currentIndex = menuItems.indexOf(currentMenuItem);
			switch (next) {
				case "next":
					nextIndex = currentIndex + 1;
					break;
				case "prev":
					nextIndex = currentIndex - 1;
					break;
				case "first":
					nextIndex = 0;
					break;
				case "last": nextIndex = menuItems.length - 1;
			}
		}
		menuItems[nextIndex]?.focus();
	};
	const handleKeydown = (event) => {
		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				focusRelativeItem(position.value === "bottom" ? "next" : "prev");
				break;
			case "ArrowUp":
				event.preventDefault();
				focusRelativeItem(position.value === "bottom" ? "prev" : "next");
				break;
			case "Home":
				event.preventDefault();
				focusRelativeItem("first");
				break;
			case "End":
				event.preventDefault();
				focusRelativeItem("last");
				break;
			case " ":
			case "Enter":
				if (event.target instanceof HTMLInputElement) break;
				event.preventDefault();
				event.target.click();
				break;
			case "Escape":
				event.preventDefault();
				setExpanded(false);
		}
	};
	const triggerEvents = computed(() => {
		if (toValue(options.trigger) !== "hover") return;
		return {
			onMouseenter: () => setExpanded(true),
			onMouseleave: () => setExpanded(false, true)
		};
	});
	useOutsideClick({
		inside: rootRef,
		onOutsideClick: () => setExpanded(false),
		disabled: computed(() => !options.isExpanded.value),
		checkOnTab: true
	});
	return { elements: {
		root: computed(() => ({
			id: rootId,
			onKeydown: handleKeydown,
			ref: rootRef,
			...triggerEvents.value
		})),
		button: computed(() => ({
			"aria-controls": menuId,
			"aria-expanded": options.isExpanded.value,
			"aria-haspopup": true,
			onFocus: () => setExpanded(true, true),
			onClick: () => toValue(options.trigger) == "click" ? setExpanded(!options.isExpanded.value) : void 0,
			id: buttonId,
			disabled: options.disabled?.value
		})),
		menu: {
			id: menuId,
			ref: menuRef,
			role: "menu",
			"aria-labelledby": buttonId,
			onClick: () => setExpanded(false)
		},
		...createMenuItems().elements
	} };
});
var createMenuItems = createBuilder((options) => {
	const { isExpanded, isExternal, disabled, externalChildrenRef, onOpen, onClose, onFocusTrigger, onHoverEnterParent, onHoverLeaveParent, openingArrowDirection = "ArrowRight" } = options || {};
	const debouncedClose = debounce(() => {
		if (isExpanded) isExpanded.value = false;
	}, 300);
	onBeforeUnmount(() => {
		debouncedClose.abort();
	});
	const handleClose = () => {
		debouncedClose.abort();
		if (isExpanded) isExpanded.value = false;
		onClose?.();
	};
	const handleTriggerMouseEnter = (event) => {
		if (toValue(isExternal) && !toValue(disabled)) {
			if (event?.type === "focusin") {
				const focusEvent = event;
				const relatedTarget = focusEvent.relatedTarget;
				const target = focusEvent.target;
				const externalNode = toValue(externalChildrenRef);
				if (!externalNode?.contains(target) && relatedTarget && externalNode?.contains(relatedTarget)) {
					debouncedClose.abort();
					return;
				}
			}
			debouncedClose.abort();
			if (isExpanded) isExpanded.value = true;
		}
	};
	const handleTriggerMouseLeave = () => {
		if (toValue(isExternal)) {
			debouncedClose.abort();
			debouncedClose();
		}
	};
	const handlePopoverMouseEnter = (event) => {
		handleTriggerMouseEnter(event);
		onHoverEnterParent?.();
	};
	const handlePopoverMouseLeave = () => {
		handleTriggerMouseLeave();
		onHoverLeaveParent?.();
	};
	const onKeydown = (event) => {
		const resolvedKey = toValue(openingArrowDirection);
		switch (event.key) {
			case resolvedKey:
			case " ":
			case "Enter":
				event.preventDefault();
				onOpen?.();
		}
	};
	const getClosingArrowDirection = () => {
		return toValue(openingArrowDirection) === "ArrowRight" ? "ArrowLeft" : "ArrowRight";
	};
	const onBackButtonKeydown = (event) => {
		if ([
			getClosingArrowDirection(),
			" ",
			"Enter"
		].includes(event.key)) {
			event.preventDefault();
			event.stopPropagation();
			handleClose();
		}
	};
	const onExternalChildrenKeydown = (event) => {
		const closeKey = getClosingArrowDirection();
		if (event.key === closeKey) {
			event.preventDefault();
			event.stopPropagation();
			onFocusTrigger?.();
		}
	};
	return {
		elements: {
			listItem: {
				role: "none",
				onMouseenter: handleTriggerMouseEnter,
				onMouseleave: handleTriggerMouseLeave,
				onFocusin: handleTriggerMouseEnter,
				onFocusout: handleTriggerMouseLeave
			},
			menuItem: (data) => ({
				"aria-current": data.active ? "page" : void 0,
				"aria-disabled": data.disabled,
				role: "menuitem",
				onKeydown
			}),
			internalChildren: { role: "menu" },
			backButton: {
				onKeydown: onBackButtonKeydown,
				onClick: (event) => {
					event.stopPropagation();
					handleClose();
				}
			},
			externalChildren: {
				role: "presentation",
				tabindex: -1,
				onKeydown: onExternalChildrenKeydown,
				onMouseenter: handlePopoverMouseEnter,
				onMouseleave: handlePopoverMouseLeave,
				onFocusin: handlePopoverMouseEnter,
				onFocusout: handlePopoverMouseLeave
			}
		},
		internals: {
			handlePopoverMouseEnter,
			handlePopoverMouseLeave
		}
	};
});
//#endregion
//#region src/utils/math.ts
var MathUtils = {
	/**
	* Ensures that a given `number` is or is between a given `min` and `max`.
	*/
	clamp: (number, min, max) => Math.max(Math.min(number, max), min),
	/**
	* Returns the count of decimal places in a number.
	*
	* @param number - The number to check.
	* @returns The count of decimal places.
	* decimals(1.23); // 2
	* decimals(10);   // 0
	*/
	decimalsCount: (number) => String(number).split(".")[1]?.length ?? 0,
	/**
	* Converts a value within a range to a percentage (0-100).
	*
	* @param value - The value to convert.
	* @param min - The minimum allowed value.
	* @param max - The maximum allowed value.
	* @returns The percentage representation of the value.
	*/
	valueToPercent: (value, min, max) => (value - min) * 100 / (max - min),
	/**
	* Converts a percentage (0-100) to a value within a range.
	*
	* @param percent - The percentage to convert.
	* @param min - The minimum allowed value.
	* @param max - The maximum allowed value.
	* @returns The value representation of the percentage.
	*/
	percentToValue: (percent, min, max) => (max - min) * percent + min
};
//#endregion
//#region src/composables/navigationMenu/createMenu.ts
/**
* Based on https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/
*/
var createNavigationMenu = createBuilder(({ navigationName }) => {
	const navId = useId();
	const getMenuButtons = () => {
		const nav = navId ? document.getElementById(navId) : void 0;
		if (!nav) return [];
		return Array.from(nav.querySelectorAll("button[aria-expanded][aria-controls]"));
	};
	const focusRelative = (trigger, next) => {
		const menuButtons = getMenuButtons();
		const index = menuButtons.indexOf(trigger);
		if (index === -1) return;
		menuButtons[MathUtils.clamp(index + (next === "next" ? 1 : -1), 0, menuButtons.length - 1)]?.focus();
	};
	return { elements: { nav: {
		"aria-label": unref(navigationName),
		id: navId,
		onKeydown: (event) => {
			switch (event.key) {
				case "ArrowRight":
					focusRelative(event.target, "next");
					break;
				case "ArrowLeft": focusRelative(event.target, "previous");
			}
		}
	} } };
});
//#endregion
//#region src/utils/array.ts
var areArraysEqual = (arrayA, arrayB, comparer = (a, b) => a === b) => arrayA.length === arrayB.length && arrayA.every((value, index) => comparer(value, arrayB[index]));
//#endregion
//#region src/composables/slider/createSlider.ts
var NAVIGATION_KEYS = /* @__PURE__ */ new Set([
	"ArrowUp",
	"ArrowDown",
	"ArrowLeft",
	"ArrowRight",
	"PageUp",
	"PageDown",
	"Home",
	"End"
]);
var INCREMENT_KEYS = /* @__PURE__ */ new Set([
	"ArrowRight",
	"ArrowUp",
	"PageUp"
]);
var DECREMENT_KEYS = /* @__PURE__ */ new Set([
	"ArrowLeft",
	"ArrowDown",
	"PageDown"
]);
/**
* Composable for creating an accessibility-compliant slider.
* For supported keyboard shortcuts, see: https://www.w3.org/WAI/ARIA/apg/patterns/slider/
*/
var createSlider = createBuilder((options) => {
	const sliderRef = createElRef();
	const firstThumbRef = createElRef();
	const secondThumbRef = createElRef();
	const min = computed(() => toValue(options.min) ?? 0);
	const max = computed(() => toValue(options.max) ?? 100);
	const step = computed(() => toValue(options.step) ?? 1);
	/**
	* Index of the thumb that is currently dragged.
	*/
	const draggingThumbIndex = ref();
	watch(draggingThumbIndex, (newThumbIndex) => {
		if (newThumbIndex == void 0) return;
		focusThumb(newThumbIndex);
	});
	const focusThumb = (index) => {
		if (index === 0) firstThumbRef.value?.focus();
		else if (index === 1) secondThumbRef.value?.focus();
	};
	const shiftStep = computed(() => {
		const shiftStep = toValue(options.shiftStep);
		if (shiftStep != void 0) return shiftStep;
		return Math.max(1, Math.round((max.value - min.value) * .1 / step.value)) * step.value;
	});
	/**
	* Normalizes the given slider (values) by ensuring that:
	* 1. Value is between min and max range
	* 2. Values are matching the `step` property (are multiples of it)
	* 3. Are sorted ascending (if range mode)
	*/
	const getNormalizedValue = computed(() => {
		return (value) => {
			let values = typeof value === "number" ? [value] : value;
			values = values.map((value) => MathUtils.clamp(value, min.value, max.value)).map((value) => {
				const stepDecimals = MathUtils.decimalsCount(step.value);
				return Number((Math.round((value - min.value) / step.value) * step.value + min.value).toFixed(stepDecimals));
			}).sort((a, b) => a - b);
			return values;
		};
	});
	/**
	* Current slider value(s) normalized to an array.
	*/
	const normalizedValue = computed(() => getNormalizedValue.value(toValue(options.value)));
	/**
	* Updates the current value with the given value. Will normalize the value.
	*/
	const updateValue = (value, index) => {
		const currentValue = normalizedValue.value.slice();
		const otherIndex = index === 0 ? 1 : 0;
		const otherValue = currentValue[otherIndex];
		if (otherValue != void 0) {
			if (index < otherIndex && value > otherValue) value = otherValue;
			else if (index > otherIndex && value < otherValue) value = otherValue;
		}
		currentValue[index] = value;
		const normalized = getNormalizedValue.value(currentValue);
		const newValue = normalized.length > 1 ? normalized : normalized[0];
		if (areArraysEqual(normalized, normalizedValue.value)) return;
		options.onChange?.(newValue);
	};
	/**
	* Increases / decreases the value by a single step. Will also ensure focus on the thumb.
	* Useful if e.g. adding custom buttons for changing the slider value.
	*/
	const updateValueByStep = (direction) => {
		const index = 0;
		const stepValue = direction === "increase" ? step.value : -step.value;
		const currentValue = normalizedValue.value[index];
		updateValue(currentValue + stepValue, index);
		focusThumb(index);
	};
	/**
	* Gets the given value in percentage relative to the sliders min/max range.
	*/
	const getValueInPercentage = computed(() => {
		return (value) => {
			const percentage = MathUtils.valueToPercent(value, min.value, max.value);
			return MathUtils.clamp(percentage, 0, 100);
		};
	});
	const handleKeydown = (event, thumbIndex) => {
		if (!NAVIGATION_KEYS.has(event.key)) return;
		event.preventDefault();
		const currentValue = normalizedValue.value.slice();
		if (currentValue[thumbIndex] == void 0) return;
		const stepSize = event.shiftKey ? shiftStep.value : step.value;
		if (event.key === "Home") return updateValue(min.value, thumbIndex);
		if (event.key === "End") return updateValue(max.value, thumbIndex);
		if (INCREMENT_KEYS.has(event.key)) updateValue(currentValue[thumbIndex] + stepSize, thumbIndex);
		else if (DECREMENT_KEYS.has(event.key)) updateValue(currentValue[thumbIndex] - stepSize, thumbIndex);
	};
	const handlePointerdown = (event) => {
		event.preventDefault();
		const value = getValueFromCoordinates(event.x);
		if (value == void 0) return;
		const thumb = normalizedValue.value.reduce((previous, thumbValue, index) => {
			const distance = Math.abs(thumbValue - value);
			if (distance < previous.distance) return {
				index,
				distance
			};
			return previous;
		}, {
			index: 0,
			distance: Number.POSITIVE_INFINITY
		});
		updateValue(value, thumb.index);
		draggingThumbIndex.value = thumb.index;
	};
	const handlePointermove = (event) => {
		if (draggingThumbIndex.value == void 0) return;
		const value = getValueFromCoordinates(event.x);
		if (value == void 0) return;
		updateValue(value, draggingThumbIndex.value);
	};
	useGlobalEventListener({
		type: "pointermove",
		listener: handlePointermove,
		disabled: computed(() => draggingThumbIndex.value == void 0)
	});
	const handlePointerup = () => {
		draggingThumbIndex.value = void 0;
	};
	useGlobalEventListener({
		type: "pointerup",
		listener: handlePointerup,
		disabled: computed(() => draggingThumbIndex.value == void 0)
	});
	/**
	* Gets the corresponding slider value for the given x coordinate across the rail.
	*/
	const getValueFromCoordinates = (x) => {
		const rect = sliderRef.value?.getBoundingClientRect();
		if (!rect || rect.width <= 0) return;
		const percent = MathUtils.clamp((x - rect.left) / rect.width, 0, 1);
		return MathUtils.percentToValue(percent, min.value, max.value);
	};
	return {
		elements: {
			/**
			* Root slider container element
			*/
			root: computed(() => {
				const events = { onPointerdown: handlePointerdown };
				return {
					ref: sliderRef,
					style: { touchAction: "pan-y" },
					...toValue(options.disabled) ? void 0 : events
				};
			}),
			/**
			* Input inside each thumb for accessibility
			*/
			thumbInput: computed(() => (data) => {
				return {
					min: min.value,
					max: max.value,
					value: data.value,
					role: "slider",
					type: "range",
					"aria-label": toValue(options.label),
					"aria-valuemin": min.value,
					"aria-valuemax": max.value,
					"aria-valuenow": data.value,
					"aria-orientation": "horizontal",
					step: step.value,
					disabled: toValue(options.disabled),
					ref: data.index === 0 ? firstThumbRef : data.index === 1 ? secondThumbRef : void 0,
					...toValue(options.disabled) ? void 0 : { onKeydown: (event) => handleKeydown(event, data.index) }
				};
			})
		},
		state: {
			normalizedValue,
			shiftStep,
			/**
			* Track element representing the selected range
			*/
			track: computed(() => {
				const start = normalizedValue.value.length > 1 ? normalizedValue.value[0] : min.value;
				const startPercentage = getValueInPercentage.value(start);
				const end = normalizedValue.value.at(-1);
				return {
					start,
					startPercentage,
					end,
					endPercentage: getValueInPercentage.value(end)
				};
			})
		},
		internals: {
			getValueInPercentage,
			updateValue,
			updateValueByStep
		}
	};
});
//#endregion
//#region src/composables/tabs/createTabs.ts
/**
* Composable for implementing accessible tabs.
* Based on https://www.w3.org/WAI/ARIA/apg/patterns/tabs/
*/
var createTabs = createBuilder((options) => {
	/**
	* Map for looking up tab and panel IDs for given tab keys/values defined by the user.
	* Key = custom value from the user, value = random generated tab and panel ID
	*/
	const idMap = /* @__PURE__ */ new Map();
	const getId = (value) => {
		if (!idMap.has(value)) idMap.set(value, {
			tabId: useId(),
			panelId: useId()
		});
		return idMap.get(value);
	};
	const handleKeydown = (event) => {
		const tab = event.target;
		const enabledTabs = Array.from(tab.parentElement?.querySelectorAll("[role=\"tab\"]") ?? []).filter((tab) => tab.ariaDisabled !== "true");
		const currentTabIndex = enabledTabs.indexOf(tab);
		const focusElement = (element) => {
			if (element instanceof HTMLElement) element.focus();
		};
		const focusFirstTab = () => focusElement(enabledTabs.at(0));
		const focusLastTab = () => focusElement(enabledTabs.at(-1));
		/**
		* Focuses the next/previous tab. Will ignore/skip disabled ones.
		*/
		const focusTab = (direction) => {
			if (currentTabIndex === -1) return;
			const newIndex = direction === "next" ? currentTabIndex + 1 : currentTabIndex - 1;
			if (newIndex < 0) return focusLastTab();
			else if (newIndex >= enabledTabs.length) return focusFirstTab();
			return focusElement(enabledTabs.at(newIndex));
		};
		switch (event.key) {
			case "ArrowRight":
				focusTab("next");
				break;
			case "ArrowLeft":
				focusTab("previous");
				break;
			case "Home":
				focusFirstTab();
				break;
			case "End":
				focusLastTab();
				break;
			case "Enter":
			case " ": {
				const tabEntry = Array.from(idMap.entries()).find(([, { tabId }]) => tabId === tab.id);
				if (tabEntry) options.onSelect?.(tabEntry[0]);
			}
		}
	};
	return { elements: {
		tablist: computed(() => ({
			role: "tablist",
			"aria-label": unref(options.label),
			onKeydown: handleKeydown
		})),
		tab: computed(() => {
			return (data) => {
				const { tabId: selectedTabId } = getId(unref(options.selectedTab));
				const { tabId, panelId } = getId(data.value);
				const isSelected = tabId === selectedTabId;
				return {
					id: tabId,
					role: "tab",
					"aria-selected": isSelected,
					"aria-controls": panelId,
					"aria-disabled": data.disabled ? true : void 0,
					onClick: () => options.onSelect?.(data.value),
					tabindex: isSelected && !data.disabled ? 0 : -1
				};
			};
		}),
		tabpanel: computed(() => {
			return (data) => {
				const { tabId, panelId } = getId(data.value);
				return {
					id: panelId,
					role: "tabpanel",
					"aria-labelledby": tabId
				};
			};
		})
	} };
});
//#endregion
//#region src/composables/toggleButton/createToggleButton.ts
/**
* Based on https://www.w3.org/WAI/ARIA/apg/patterns/button/#:~:text=Toggle%20button
*/
var createToggleButton = createBuilder((options) => {
	return { elements: { 
	/**
	* A html button element that is supposed to act as a toggle button.
	*/
button: computed(() => ({
		"aria-pressed": toValue(options.isPressed),
		onClick: options.onToggle
	})) } };
});
//#endregion
//#region src/composables/helpers/useDismissible.ts
/**
* Composable that sets `isExpanded` to false, when the `Escape` key is pressed. Addresses the
* "dismissible" aspect of
* https://www.w3.org/WAI/WCAG21/Understanding/content-on-hover-or-focus.html
*/
var useDismissible = ({ isExpanded }) => useGlobalEventListener({
	type: "keydown",
	listener: (e) => {
		if (e.key === "Escape") isExpanded.value = false;
	},
	disabled: computed(() => !isExpanded.value)
});
//#endregion
//#region src/composables/tooltip/createToggletip.ts
/**
* Create a toggletip as described in https://inclusive-components.design/tooltips-toggletips/
* Its visibility is toggled on click.
* Therefore a toggletip MUST NOT be used to describe the associated trigger element.
* Commonly this pattern uses a button with the ⓘ as the trigger element.
* To describe the associated element use `createTooltip`.
*/
var createToggletip = createBuilder(({ toggleLabel, isVisible }) => {
	const triggerId = useId();
	const _isVisible = toRef(isVisible ?? false);
	useDismissible({ isExpanded: _isVisible });
	const toggle = () => _isVisible.value = !_isVisible.value;
	return {
		elements: {
			/**
			* The element which controls the toggletip visibility:
			* Preferably a `button` element.
			*/
			trigger: computed(() => ({
				id: triggerId,
				onClick: toggle,
				"aria-label": toValue(toggleLabel)
			})),
			/**
			* The element with the relevant toggletip content.
			* Only simple, textual content is allowed.
			*/
			tooltip: {
				onToggle: (e) => {
					const tooltip = e.target;
					_isVisible.value = tooltip.matches(":popover-open");
				},
				anchor: triggerId,
				popover: "auto",
				role: "status",
				tabindex: "-1"
			}
		},
		state: { isVisible: _isVisible }
	};
});
//#endregion
//#region src/composables/tooltip/createTooltip.ts
/**
* Create a tooltip as described in
* https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/tooltip_role Its visibility
* is toggled on hover or focus. A tooltip MUST be used to describe the associated trigger element.
* E.g. The usage with the ⓘ would be incorrect. To provide contextual information use the
* `createToggletip`.
*/
var createTooltip = createBuilder(({ debounce, isVisible }) => {
	const tooltipId = useId();
	const _isVisible = toRef(isVisible ?? false);
	let timeout;
	/**
	* Debounced visible state that will only be toggled after a given timeout.
	*/
	const debouncedVisible = computed({
		get: () => _isVisible.value,
		set: (newValue) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => {
				_isVisible.value = newValue;
			}, toValue(debounce));
		}
	});
	const hoverEvents = {
		onMouseover: () => debouncedVisible.value = true,
		onMouseout: () => debouncedVisible.value = false,
		onFocusin: () => _isVisible.value = true,
		onFocusout: () => _isVisible.value = false
	};
	useDismissible({ isExpanded: _isVisible });
	return {
		elements: {
			/**
			* The element which controls the tooltip visibility on hover.
			*/
			trigger: {
				"aria-describedby": tooltipId,
				...hoverEvents
			},
			/**
			* The element describing the tooltip.
			* Only simple, textual and non-focusable content is allowed.
			*/
			tooltip: {
				popover: "manual",
				role: "tooltip",
				id: tooltipId,
				tabindex: "-1",
				...hoverEvents
			}
		},
		state: { isVisible: _isVisible }
	};
});
//#endregion
//#region src/composables/treeView/createTreeView.ts
var createTreeView = createBuilder((options) => {
	return { elements: { tree: computed(() => ({
		role: "tree",
		"aria-label": toValue(options.label)
	})) } };
});
//#endregion
//#region src/composables/treeView/createTreeViewItem.ts
var createTreeViewItem = createBuilder((options) => {
	const disabled = computed(() => toValue(options.disabled));
	const level = computed(() => toValue(options.level));
	const hasChildren = computed(() => toValue(options.hasChildren));
	const isOpen = computed(() => toValue(options.isOpen));
	const toggleOpen = () => {
		if (disabled.value) return;
		if (hasChildren.value) options.onToggle?.(!isOpen.value);
		options.onSelect?.();
	};
	/**
	* Determines if a specific DOM element or its tree item wrapper is disabled.
	*/
	const isElementDisabled = (item) => {
		if (item.hasAttribute("disabled") || item.getAttribute("aria-disabled") === "true") return true;
		return (item.parentElement?.closest("[role=\"treeitem\"]"))?.getAttribute("aria-disabled") === "true";
	};
	/**
	* Crawls the parent tree structure to retrieve all currently visible, active tree items.
	*/
	const getVisibleTreeItems = (triggerEl) => {
		const rootTree = triggerEl.closest("[role=\"tree\"]");
		if (!rootTree) return [];
		return Array.from(rootTree.querySelectorAll("[role=\"treeitem\"]")).filter((item) => {
			if (!(item.offsetWidth > 0 || item.offsetHeight > 0 || item.getClientRects().length > 0)) return false;
			return !isElementDisabled(item);
		});
	};
	const handleKeyDown = (event) => {
		if (disabled.value) return;
		const currentTrigger = event.currentTarget;
		switch (event.key) {
			case "Enter":
			case " ":
				event.preventDefault();
				toggleOpen();
				break;
			case "ArrowRight":
				event.preventDefault();
				if (hasChildren.value) if (isOpen.value) {
					const items = getVisibleTreeItems(currentTrigger);
					const currentIndex = items.indexOf(currentTrigger);
					if (currentIndex !== -1 && currentIndex < items.length - 1) items[currentIndex + 1].focus();
				} else options.onToggle?.(true);
				break;
			case "ArrowLeft":
				event.preventDefault();
				if (hasChildren.value && isOpen.value) options.onToggle?.(false);
				else {
					const parentGroup = currentTrigger.closest("[role=\"group\"]");
					if (parentGroup) {
						const parentTrigger = parentGroup.closest("[role=\"treeitem\"]") || parentGroup.parentElement?.querySelector("[role=\"treeitem\"]");
						if (parentTrigger && parentTrigger !== currentTrigger) parentTrigger.focus();
					}
				}
				break;
			case "ArrowDown": {
				event.preventDefault();
				const items = getVisibleTreeItems(currentTrigger);
				const currentIndex = items.indexOf(currentTrigger);
				if (currentIndex !== -1 && currentIndex < items.length - 1) items[currentIndex + 1].focus();
				break;
			}
			case "ArrowUp": {
				event.preventDefault();
				const items = getVisibleTreeItems(currentTrigger);
				const currentIndex = items.indexOf(currentTrigger);
				if (currentIndex > 0) items[currentIndex - 1].focus();
				break;
			}
			case "Home": {
				event.preventDefault();
				const items = getVisibleTreeItems(currentTrigger);
				if (items.length > 0) items[0].focus();
				break;
			}
			case "End": {
				event.preventDefault();
				const items = getVisibleTreeItems(currentTrigger);
				if (items.length > 0) items[items.length - 1].focus();
				break;
			}
		}
	};
	return { elements: { treeItem: computed(() => ({
		role: "treeitem",
		"aria-expanded": hasChildren.value ? isOpen.value : void 0,
		"aria-level": level.value,
		"aria-disabled": disabled.value ? "true" : void 0,
		tabindex: disabled.value ? -1 : 0,
		onClick: toggleOpen,
		onKeydown: handleKeyDown
	})) } };
});
//#endregion
//#region src/composables/dragAndDrop/createDragAndDrop.ts
/**
* Composable for creating drag and drop functionality.
*/
var createDragAndDrop = createBuilder((options) => {
	let isDragoverAnimationFrameTicking = false;
	const onDragover = (event) => {
		event.preventDefault();
		if (!isDragoverAnimationFrameTicking) {
			requestAnimationFrame(() => {
				options.onDragover?.(event);
				isDragoverAnimationFrameTicking = false;
			});
			isDragoverAnimationFrameTicking = true;
		}
	};
	const onDrop = (event) => {
		event.preventDefault();
		options.onDrop?.(event);
	};
	const onDragstart = (event, item) => options.onDragstart?.(event, item);
	const onDragend = (event, item) => options.onDragend?.(event, item);
	return { elements: {
		/**
		* Drop target where draggable elements are allowed to be dropped.
		* drop).
		*/
		target: {
			onDragover,
			onDrop
		},
		/**
		* Drag and drop trigger / handle (e.g. an icon) that starts the drag.
		*/
		trigger: ({ item }) => ({
			draggable: true,
			onDragstart: (event) => onDragstart(event, item),
			onDragend: (event) => onDragend(event, item)
		})
	} };
});
//#endregion
export { CLOSING_KEYS, OPENING_KEYS, createBuilder, createCalendar, createComboBox, createDataGrid, createDragAndDrop, createElRef, createListbox, createMenuButton, createMenuItems, createNavigationMenu, createSlider, createTabs, createToggleButton, createToggletip, createTooltip, createTreeView, createTreeViewItem, debounce, getNativeElement, isPrintableCharacter, useGlobalEventListener, useOutsideClick, wasKeyPressed };
